import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, Loader2, ArrowLeft, Shield, BarChart3, CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const answerLabel = (questions, questionId, answerText) => {
  const q = (questions || []).find((x) => x.id === questionId);
  if (!q) return answerText;
  const opts = q.options || [];
  const hit = opts.find((o) => String(o.value) === String(answerText));
  if (hit && hit.label && hit.label !== hit.value) {
    return `${hit.value} — ${hit.label}`;
  }
  return answerText;
};

const SurveyResults = ({ surveyId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('summary'); // summary | responses

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/admin/surveys/${surveyId}/results`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        toast.error('Nie udało się pobrać wyników ankiety');
        onBack();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [surveyId, onBack]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Ładowanie wyników…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary-700 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Wróć do listy ankiet
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900">{data.title}</h2>
            {data.anonymous && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 px-2 py-1 rounded-md">
                <Shield className="h-3 w-3" /> Anonimowa
              </span>
            )}
          </div>
          {data.description && (
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">{data.description}</p>
          )}
          <p className="text-sm text-gray-600 mt-2">
            Odpowiedzi: <strong>{data.responseCount}</strong>
            {data.anonymous && (
              <span className="text-emerald-700"> · bez danych kto wypełnił</span>
            )}
          </p>
        </div>
      </div>

      <div className="inline-flex gap-1 p-1 bg-gray-100 rounded-xl mb-6">
        <button
          type="button"
          onClick={() => setView('summary')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${
            view === 'summary' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'
          }`}
        >
          Podsumowanie
        </button>
        <button
          type="button"
          onClick={() => setView('responses')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${
            view === 'responses' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'
          }`}
        >
          Poszczególne odpowiedzi
        </button>
      </div>

      {view === 'summary' ? (
        <div className="space-y-4">
          {(data.questions || []).map((q, idx) => {
            const dist = (data.distribution && data.distribution[q.id]) || [];
            const isScale = (q.questionType || '').toUpperCase() === 'SCALE'
              || (q.questionType || '').toUpperCase() === 'SINGLE_CHOICE'
              || (q.questionType || '').toUpperCase() === 'MULTI_CHOICE';
            const textAnswers = (data.responses || [])
              .flatMap((r) => (r.answers || [])
                .filter((a) => a.questionId === q.id && (a.answerText || '').trim())
                .map((a) => a.answerText));

            return (
              <div key={q.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
                <p className="text-sm font-semibold text-gray-900">
                  <span className="text-primary-600 mr-1.5">{idx + 1}.</span>
                  {q.questionText}
                </p>
                <p className="text-xs text-gray-400 mt-1 uppercase tracking-wide">{q.questionType}</p>

                {isScale && dist.length > 0 ? (
                  <div className="mt-4 space-y-2">
                    {dist.map((row) => {
                      const total = dist.reduce((s, x) => s + (x.count || 0), 0) || 1;
                      const pct = Math.round(((row.count || 0) / total) * 100);
                      const opt = (q.options || []).find((o) => String(o.value) === String(row.value));
                      const label = opt && opt.label && opt.label !== opt.value
                        ? `${row.value} — ${opt.label}`
                        : row.value;
                      return (
                        <div key={row.value}>
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>{label}</span>
                            <span className="font-semibold">{row.count} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : textAnswers.length > 0 ? (
                  <ul className="mt-3 space-y-2">
                    {textAnswers.map((t, i) => (
                      <li key={i} className="text-sm text-gray-700 bg-gray-50 rounded-xl px-3 py-2 whitespace-pre-wrap">
                        {t}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-gray-400">Brak odpowiedzi.</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {(data.responses || []).length === 0 ? (
            <div className="bg-white rounded-xl ring-1 ring-black/5 p-10 text-center text-gray-500">
              Brak odpowiedzi.
            </div>
          ) : (
            (data.responses || []).map((r, i) => (
              <div key={r.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <p className="text-sm font-semibold text-gray-900">
                    Odpowiedź #{data.responses.length - i}
                    <span className="ml-2 font-normal text-gray-500">
                      {data.anonymous ? '· Anonimowo' : `· ${r.respondent}`}
                    </span>
                  </p>
                  <p className="text-xs text-gray-400">{formatDate(r.submittedAt)}</p>
                </div>
                <div className="space-y-3">
                  {(data.questions || []).map((q) => {
                    const a = (r.answers || []).find((x) => x.questionId === q.id);
                    if (!a || !(a.answerText || '').trim()) return null;
                    return (
                      <div key={q.id} className="border-t border-gray-100 pt-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">{q.questionText}</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                          {answerLabel(data.questions, q.id, a.answerText)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const AdminSurveysPanel = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/surveys');
      setSurveys(res.data || []);
    } catch (e) {
      toast.error('Nie udało się pobrać listy ankiet');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (selectedId) {
    return (
      <SurveyResults
        surveyId={selectedId}
        onBack={() => {
          setSelectedId(null);
          load();
        }}
      />
    );
  }

  return (
    <div>
      <div className="mb-4">
        <p className="text-sm text-gray-500">
          Wyniki ankiet z portalu szkoleń / ankiet. Ankiety anonimowe nie zawierają danych respondenta.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : surveys.length === 0 ? (
        <div className="bg-white rounded-xl ring-1 ring-black/5 p-10 text-center text-gray-500">
          <ClipboardList className="h-10 w-10 mx-auto text-gray-300 mb-3" />
          Brak ankiet w systemie.
        </div>
      ) : (
        <div className="space-y-3">
          {surveys.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-5 flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{s.title}</h3>
                  {s.anonymous && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 px-2 py-1 rounded-md">
                      <Shield className="h-3 w-3" /> Anonimowa
                    </span>
                  )}
                  {s.active ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-green-700 bg-green-100 px-2 py-1 rounded-md">
                      <CheckCircle2 className="h-3 w-3" /> Aktywna
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                      Nieaktywna
                    </span>
                  )}
                </div>
                {s.description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{s.description}</p>
                )}
                <p className="text-sm text-gray-600 mt-2">
                  Odpowiedzi: <strong>{s.responseCount ?? 0}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedId(s.id)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100"
              >
                <BarChart3 className="h-4 w-4" /> Wyniki
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSurveysPanel;
